// One quality alert, in full: where it stands, what it is, what it looks
// like, and what has been said about it — with the same four things the Odoo
// form lets a person do to it, under their own access rights.
//
// Stage and category are written directly, as the form writes them; a photo is
// an attachment on the alert, in one of three strips — Before, After, Other
// (interpod_quality's picture buckets; Other is what sits in neither); a
// message is a note on its chatter. Odoo still decides who may: a stage the
// alert's team does not own is refused there, and this sheet only asks the
// questions the form would.
//
// A team can require a before AND an after picture before an alert closes.
// Odoo refuses the done stage without them; this sheet turns Close into the
// camera for the missing picture instead, so the refusal is never met.

import { useEffect, useMemo, useRef, useState } from 'react';
import { attachmentImageUrl, type PictureBucket } from '@/api/client';
import type { AlertVM } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';
import { AvatarTile, EmptyState, Skeleton } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import { downscaleToJpeg } from '@/lib/image';
import { fmtLocalTime } from '@/lib/pace';
import { canCloseAlert, canEditAlert } from '@/lib/qualityAccess';
import { andonHref, useAlert, useAlertActions, useAlertMessages, useAndonsByIds, useDefectCategories, useQualityStages, useTeams } from '@/queries';
import { useStore } from '@/store';

/** Where the alert opens in Odoo — the same relative path the andon rows fall back to. */
export const qualityAlertHref = (id: number): string =>
  `/odoo/action-base.action_ui_view?model=quality.alert&id=${id}&view_type=form`;

export function QualitySheet({
  open,
  alertId,
  onClose,
  onRaiseAndon,
}: {
  open: boolean;
  alertId: number | null;
  onClose: () => void;
  /**
   * Raise an andon about this alert's pod. The shell decides what that is —
   * its own raise sheet, or the hand-off to the Andon app — so this sheet
   * only has to say which pod.
   */
  onRaiseAndon: (moId: number | null, mo: string | null) => void;
}) {
  const t = useT();
  const isLeader = useStore((s) => s.isLeader);
  const operatorId = useStore((s) => s.operator?.id ?? null);
  const connMode = useStore((s) => s.connMode);
  const userUid = useStore((s) => s.userUid);
  const operatorName = useStore((s) => s.operator?.name ?? '');
  const showToast = useStore((s) => s.showToast);
  const andonPreview = useStore((s) => s.andonCaps.preview);
  const andonAppUrl = useStore((s) => s.andonAppUrl);
  const openAndon = useStore((s) => s.openAndon);

  const alert = useAlert(open ? alertId : null);
  const q = alert.data ?? null;
  const stages = useQualityStages(q?.teamId ?? null);
  const teams = useTeams();
  const cats = useDefectCategories();
  const messages = useAlertMessages(open ? alertId : null);
  const actions = useAlertActions(alertId);
  // The other half of the pair: the andon this alert was raised from. Read
  // only when there is one, which is the uncommon case.
  const linked = useAndonsByIds(open ? (q?.andonAlertIds ?? []) : []);

  const [draft, setDraft] = useState('');
  // The lightbox: which strip it was opened from and where in it, so
  // previous / next walk that strip and Move knows which bucket to hide.
  const [viewing, setViewing] = useState<{ bucket: PictureBucket; index: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // The bucket the next capture goes into — set by the tile (or the Close
  // button) that opened the camera, read when the file arrives.
  const captureBucket = useRef<PictureBucket>('other');
  useEffect(() => {
    if (open) {
      setDraft('');
      setViewing(null);
    }
  }, [open, alertId]);

  // Every alert on a pod is the floor's to work on: anyone signed in may move
  // it between the open stages, correct the defect, add a photo or a note.
  // Closing is Odoo's to grant — only an approver of the alert's team, signed
  // in as themselves — so the close is offered to exactly that person and the
  // reason is said to everyone else (see lib/qualityAccess).
  const session = { connMode, userUid, operatorId, isLeader };
  const canEdit = !!q && canEditAlert(session);
  const team = (teams.data ?? []).find((tm) => tm.id === q?.teamId);
  const closeVerdict = canCloseAlert(session, team?.approverUids);
  const canClose = canEdit && closeVerdict.ok;
  const stage = (stages.data ?? []).find((s) => s.id === q?.stageId) ?? null;
  const closed = stage?.done ?? false;

  // The sibling defects of the one on the alert: the correction a floor sheet
  // can make is "wrong chip in the right family", not a search of the whole
  // taxonomy — that is the raise form's job.
  const siblings = useMemo(() => {
    const all = cats.data ?? [];
    if (!q) return [];
    const parentId = q.categoryId ?? all.find((c) => c.id === q.subcategoryId)?.parentId ?? null;
    return parentId == null ? [] : all.filter((c) => c.parentId === parentId);
  }, [cats.data, q]);

  const refuse = () => showToast(t.onlyRaiserOrLeaderT);
  const whyNotClose = closeVerdict.ok ? '' : closeVerdict.reason === 'notApprover' ? t.onlyApproverCanCloseT : t.closeFromOdooAccountT;
  const refuseClose = () => showToast(whyNotClose);

  // The three strips. Other is Odoo's `picture_ids`, which excludes whatever
  // sits in a bucket, so a picture is in exactly one of them.
  const strips: Array<{ bucket: PictureBucket; label: string; ids: number[] }> = q
    ? [
        { bucket: 'before', label: t.beforeT, ids: q.beforePictureIds },
        { bucket: 'after', label: t.afterT, ids: q.afterPictureIds },
        { bucket: 'other', label: t.otherT, ids: q.pictureIds },
      ]
    : [];
  const stripOf = (bucket: PictureBucket) => strips.find((st) => st.bucket === bucket)?.ids ?? [];
  const viewingId = viewing ? (stripOf(viewing.bucket)[viewing.index] ?? null) : null;

  // The team's picture rule, as the close sees it. The approver rule
  // (closeVerdict) comes first: whoever may not close is told that, and the
  // camera is offered only to the person who could then close.
  const needsBefore = !!q && q.requirePictures && q.beforePictureIds.length === 0;
  const needsAfter = !!q && q.requirePictures && q.afterPictureIds.length === 0;
  const picturesMissing = needsBefore || needsAfter;
  const picturesToCloseLabel = needsBefore ? t.addBeforeAfterPicturesToCloseT : t.addAfterPictureToCloseT;

  const openCamera = (bucket: PictureBucket) => {
    captureBucket.current = bucket;
    fileRef.current?.click();
  };

  const closeAlert = () => {
    if (!q) return;
    const done = (stages.data ?? []).filter((s) => s.done);
    const target = done.find((s) => /^closed$/i.test(s.name)) ?? done[0];
    if (!target) return;
    actions.setStage.mutate(target.id, {
      onSuccess: () => {
        showToast(`${q.qa} → ${target.name}`);
        onClose();
      },
    });
  };

  const post = () => {
    const body = draft.trim();
    if (!body || !q) return;
    actions.post.mutate(body, { onSuccess: () => setDraft('') });
  };

  /**
   * THE OTHER HALF OF THE PAIR, when there is none yet (frame script L919
   * `Raise an andon on this`, L932 `qaAndon`): an andon with this alert's
   * pod already on it. The link between the two is the server's to make —
   * see `quality_alert_id` on the andon — so what travels is the pod, which
   * is the one thing the raise would otherwise ask for.
   */
  const raiseAndonOnThis = () => {
    if (!q) return;
    onClose();
    onRaiseAndon(q.moId, q.mo);
  };

  const onPhoto = async (file: File | undefined) => {
    if (!file || !q) return;
    const bucket = captureBucket.current;
    const base64 = await downscaleToJpeg(file);
    // Into the bucket whose tile opened the camera. The refetch the success
    // triggers is what moves Close back from "add an after picture".
    actions.attach.mutate(
      { name: `${q.qa}${bucket === 'other' ? '' : `-${bucket}`}.jpg`, base64, bucket },
      { onSuccess: () => showToast(`${t.photoAttachedToT} ${q.qa}`) },
    );
  };

  const movePicture = (attachmentId: number, bucket: PictureBucket) => {
    if (!q) return;
    actions.movePicture.mutate(
      { attachmentId, bucket },
      {
        onSuccess: () => {
          // The picture has left the strip the lightbox was walking.
          setViewing(null);
          showToast(`${q.qa} · ${t.pictureMovedT}`);
        },
      },
    );
  };

  const step = (dir: -1 | 1) => {
    if (!viewing) return;
    const ids = stripOf(viewing.bucket);
    const next = viewing.index + dir;
    if (next >= 0 && next < ids.length) setViewing({ bucket: viewing.bucket, index: next });
  };
  // Swipe on touch: a horizontal travel past the threshold is a step; a tap
  // (no travel) stays a tap on whatever it landed on.
  const touchStartX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const from = touchStartX.current;
    touchStartX.current = null;
    const to = e.changedTouches[0]?.clientX;
    if (from == null || to == null) return;
    const dx = to - from;
    if (Math.abs(dx) < 40) return;
    step(dx < 0 ? 1 : -1);
  };

  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={q?.title ?? t.qualityT} hideTitle>
      {alert.isPending || !q ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <Skeleton height={48} radius={12} />
          <Skeleton height={120} radius={12} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', font: "600 12px 'Space Grotesk',sans-serif", color: 'var(--sub)' }}>
                {[q.qa, q.mo, q.team].filter(Boolean).join(' · ')}
              </span>
              <h2 style={{ font: "700 19px 'Space Grotesk',sans-serif", margin: '2px 0 0', lineHeight: 1.25 }}>{q.title}</h2>
            </span>
            <span
              style={{
                borderRadius: 7,
                padding: '3px 9px',
                font: '700 10.5px Manrope,sans-serif',
                background: closed ? 'var(--posSoft)' : 'var(--warnSoft)',
                color: closed ? 'var(--pos)' : 'var(--warn)',
                flex: 'none',
                marginTop: 4,
              }}
            >
              {q.stage}
            </span>
          </div>

          <Facts
            rows={[
              [t.raisedByL, `${q.employeeName || '—'} · ${fmtLocalTime(q.createdAt)}`],
              [t.teamT, q.team],
              [t.signOffT, q.preventMoSignoff ? t.blocksMoSignoffUntilClosedT : t.doesNotBlockT],
              ...(q.workcentreName ? [[t.workcentre, q.workcentreName] as [string, string]] : []),
            ]}
          />

          {q.description && (
            <div style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--ink)', lineHeight: 1.5, background: 'var(--surface2)', borderRadius: 10, padding: '10px 12px', whiteSpace: 'pre-wrap' }}>
              {q.description}
            </div>
          )}

          {/* THE LINKED ANDON. An alert raised from an andon carries it, and
              the person reading the defect wants the stopped line's story one
              tap away. With the preview on that is this app's own thread;
              without it, the Andon app's. A closed andon reads "Closed" — the
              frame has no Resolved state. */}
          {q.andonAlertIds.length > 0 && (
            <div>
              <Label>{t.linkedAndonT}</Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(linked.data ?? []).map((a) => {
                  const state = a.state === 'new' ? t.unansweredT : a.state === 'closed' ? (andonPreview ? t.closedStateT : t.resolvedT) : a.state === 'cancelled' ? t.cancelledT : t.respondingTagT;
                  return andonPreview ? (
                    <button
                      key={a.id}
                      type="button"
                      className="press-row"
                      onClick={() => {
                        onClose();
                        openAndon(a.id);
                      }}
                      style={linkedRow}
                    >
                      <LinkedAndon refName={a.ref} title={a.title} state={state} />
                      <Icon name="chevron" size={14} strokeWidth={2.4} style={{ color: 'var(--sub)' }} />
                    </button>
                  ) : (
                    <a key={a.id} href={andonHref(andonAppUrl, a.id)} target="_blank" rel="noopener" className="press-row" style={linkedRow}>
                      <LinkedAndon refName={a.ref} title={a.title} state={state} />
                      <Icon name="external" size={13} style={{ color: 'var(--sub)' }} />
                    </a>
                  );
                })}
                {linked.isPending && <Skeleton height={44} radius={12} />}
              </div>
            </div>
          )}

          {/* No andon yet: the verb that makes the pair (frame script L919).
              Not on a closed alert — nobody needs to come now for a defect
              that is dealt with. */}
          {q.andonAlertIds.length === 0 && !closed && (
            <button
              type="button"
              className="press"
              onClick={raiseAndonOnThis}
              style={{ alignSelf: 'flex-start', minHeight: 40, padding: '0 13px', borderRadius: 11, background: 'var(--dangerSoft)', color: 'var(--danger)', border: '1px solid transparent', font: '700 12px Manrope,sans-serif' }}
            >
              {t.raiseAndonOnThisT}
            </button>
          )}

          <div>
            <Label>{`${t.stageT} · ${q.team}`}</Label>
            <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 999, padding: 3, overflowX: 'auto' }}>
              {(stages.data ?? []).map((s) => {
                const selected = s.id === q.stageId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    className="press"
                    aria-pressed={selected}
                    onClick={() =>
                      !canEdit
                        ? refuse()
                        : s.done && !canClose
                          ? refuseClose()
                          : s.done && picturesMissing
                            // Odoo would refuse the done stage without the
                            // pictures; the camera is the answer, not the error.
                            ? openCamera(needsBefore ? 'before' : 'after')
                            : actions.setStage.mutate(s.id, { onSuccess: () => showToast(`${q.qa} → ${s.name}`) })
                    }
                    style={{
                      flex: 'none',
                      borderRadius: 999,
                      padding: '7px 11px',
                      minHeight: 34,
                      font: '700 11px Manrope,sans-serif',
                      background: selected ? 'var(--ink)' : 'transparent',
                      color: selected ? 'var(--bg)' : 'var(--sub)',
                    }}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>

          {siblings.length > 0 && (
            <div>
              <Label sub={q.category}>{t.categoryT}</Label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {siblings.map((c) => {
                  const selected = c.id === q.subcategoryId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className="press"
                      aria-pressed={selected}
                      onClick={() =>
                        canEdit
                          ? actions.setCategory.mutate(
                              { categoryId: c.parentId, subcategoryId: c.id },
                              { onSuccess: () => showToast(`${q.qa} · ${t.categoryUpdatedT}`) },
                            )
                          : refuse()
                      }
                      style={{
                        borderRadius: 999,
                        padding: '6px 10px',
                        minHeight: 32,
                        font: '600 11px Manrope,sans-serif',
                        background: selected ? 'var(--ink)' : 'var(--surface)',
                        color: selected ? 'var(--bg)' : 'var(--ink)',
                        border: `1px solid ${selected ? 'var(--ink)' : 'var(--line)'}`,
                      }}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* THE PICTURES, in three strips. Each has its own camera tile so a
              photo lands in the bucket it was taken for; a closed alert shows
              them read-only. The lightbox a thumbnail opens walks its strip. */}
          {strips.map((st) => (
            <div key={st.bucket}>
              <Label>{`${st.label} · ${st.ids.length}`}</Label>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                {st.ids.map((id, i) => (
                  <button
                    key={id}
                    type="button"
                    className="press"
                    onClick={() => setViewing({ bucket: st.bucket, index: i })}
                    aria-label={`${st.label} · ${t.photoOpensT} ${i + 1}`}
                    style={{ flex: 'none', width: 96, height: 72, borderRadius: 10, overflow: 'hidden', background: 'var(--surface2)' }}
                  >
                    <img src={attachmentImageUrl(id)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
                {!closed && (
                  <button
                    type="button"
                    className="press"
                    onClick={() => (canEdit ? openCamera(st.bucket) : refuse())}
                    disabled={actions.attach.isPending}
                    aria-label={`${t.addPhotoT} · ${st.label}`}
                    style={{
                      flex: 'none',
                      width: 96,
                      height: 72,
                      borderRadius: 10,
                      border: `1.5px dashed ${st.bucket !== 'other' && q.requirePictures && st.ids.length === 0 ? 'var(--accent)' : 'var(--line)'}`,
                      color: 'var(--sub)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      font: '700 11px Manrope,sans-serif',
                    }}
                  >
                    <Icon name="camera" size={18} />
                    {t.addPhotoT}
                  </button>
                )}
                {st.ids.length === 0 && closed && <EmptyState style={{ padding: 10, flex: 1 }}>—</EmptyState>}
              </div>
            </div>
          ))}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              void onPhoto(e.target.files?.[0]);
              e.target.value = '';
            }}
          />

          <div>
            <Label>{`${t.chatterT} · ${messages.data?.length ?? 0}`}</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
              {messages.isPending ? (
                <Skeleton height={44} radius={10} />
              ) : (messages.data ?? []).length === 0 ? (
                <EmptyState style={{ padding: 10 }}>—</EmptyState>
              ) : (
                (messages.data ?? []).map((m) => (
                  <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <AvatarTile name={m.authorName || '?'} size={26} />
                    <span style={{ flex: 1, minWidth: 0, background: 'var(--surface2)', borderRadius: 10, padding: '8px 10px' }}>
                      <span style={{ display: 'block', font: '700 11px Manrope,sans-serif' }}>
                        {m.authorName || '—'}{' '}
                        <span style={{ fontWeight: 500, color: 'var(--sub)' }}>· {fmtLocalTime(m.date)}</span>
                      </span>
                      <span style={{ display: 'block', font: '500 12px Manrope,sans-serif', lineHeight: 1.45, marginTop: 2, whiteSpace: 'pre-wrap' }}>
                        {m.text}
                      </span>
                    </span>
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && post()}
                placeholder={t.sendMessagePhT}
                aria-label={t.sendMessagePhT}
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 42,
                  borderRadius: 12,
                  background: 'var(--surface2)',
                  border: '1.5px solid var(--line)',
                  padding: '0 12px',
                  font: '500 13px Manrope,sans-serif',
                  color: 'var(--ink)',
                }}
              />
              <Button variant="ink" size="md" full={false} pending={actions.post.isPending} disabled={!draft.trim()} onClick={post} style={{ padding: '0 14px' }}>
                {t.postT}
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
            {canEdit && !closed && canClose && picturesMissing && (
              // Not a close yet: the team wants the pictures first, and Odoo
              // would refuse the stage without them. The button is the camera
              // for the missing one — before first if both are missing — and
              // the Close comes back once the upload refetches the alert.
              <Button
                variant="ink"
                size="md"
                pending={actions.attach.isPending}
                onClick={() => openCamera(needsBefore ? 'before' : 'after')}
                icon={<Icon name="camera" size={16} />}
              >
                {picturesToCloseLabel}
              </Button>
            )}
            {canEdit && !closed && canClose && !picturesMissing && (
              <Button variant="ink" size="md" pending={actions.setStage.isPending} onClick={closeAlert}>
                {t.closeFixedT}
              </Button>
            )}
            {canEdit && !closed && !canClose && (
              <p style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', margin: 0, textAlign: 'center' }}>
                {whyNotClose}
              </p>
            )}
            <a
              href={qualityAlertHref(q.id)}
              target="_blank"
              rel="noopener"
              className="press"
              style={{
                minHeight: 44,
                borderRadius: 13,
                border: '1px solid var(--line)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                font: '700 13px Manrope,sans-serif',
                color: 'var(--ink)',
                textDecoration: 'none',
              }}
            >
              {t.openInOdooT}
              <Icon name="external" size={13} />
            </a>
          </div>
          <span className="sr-only">{operatorName}</span>
        </div>
      )}

      {viewing != null && viewingId != null && q && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${strips.find((st) => st.bucket === viewing.bucket)?.label ?? ''} · ${viewing.index + 1} / ${stripOf(viewing.bucket).length}`}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 40,
            background: 'rgba(8,10,14,.92)',
            display: 'flex',
            flexDirection: 'column',
            padding: 16,
            gap: 12,
          }}
        >
          {/* The backdrop is the dismiss; everything else sits on top of it. */}
          <button type="button" onClick={() => setViewing(null)} aria-label={t.dismiss} style={{ position: 'absolute', inset: 0 }} />
          <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'grid', placeItems: 'center' }}>
            <img
              src={attachmentImageUrl(viewingId)}
              alt=""
              onClick={() => setViewing(null)}
              style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 12, position: 'relative' }}
            />
          </div>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <button
              type="button"
              className="press"
              onClick={() => step(-1)}
              disabled={viewing.index === 0}
              aria-label={t.previousPhotoT}
              style={{ ...lightboxArrow, opacity: viewing.index === 0 ? 0.35 : 1 }}
            >
              <Icon name="back" size={18} />
            </button>
            <span className="numeric" style={{ font: "600 12.5px 'Space Grotesk',sans-serif", color: '#fff' }}>
              {strips.find((st) => st.bucket === viewing.bucket)?.label} · {viewing.index + 1} / {stripOf(viewing.bucket).length}
            </span>
            <button
              type="button"
              className="press"
              onClick={() => step(1)}
              disabled={viewing.index >= stripOf(viewing.bucket).length - 1}
              aria-label={t.nextPhotoT}
              style={{ ...lightboxArrow, opacity: viewing.index >= stripOf(viewing.bucket).length - 1 ? 0.35 : 1 }}
            >
              <Icon name="chevron" size={18} />
            </button>
          </div>
          {/* Move between buckets — the one it already sits in is not offered.
              Not on a closed alert: its pictures are the record. */}
          {!closed && canEdit && (
            <div style={{ position: 'relative', display: 'flex', gap: 8 }}>
              {strips
                .filter((st) => st.bucket !== viewing.bucket)
                .map((st) => (
                  <Button
                    key={st.bucket}
                    variant="surface"
                    size="md"
                    pending={actions.movePicture.isPending}
                    onClick={() => movePicture(viewingId, st.bucket)}
                  >
                    {st.bucket === 'before' ? t.moveToBeforeT : st.bucket === 'after' ? t.moveToAfterT : t.moveToOtherT}
                  </Button>
                ))}
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}

const lightboxArrow: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 12,
  background: 'rgba(255,255,255,.12)',
  color: '#fff',
  display: 'grid',
  placeItems: 'center',
  flex: 'none',
};

const linkedRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  minHeight: 44,
  padding: '8px 12px',
  borderRadius: 12,
  background: 'var(--surface2)',
  color: 'var(--ink)',
  textAlign: 'left',
  textDecoration: 'none',
};

function LinkedAndon({ refName, title, state }: { refName: string; title: string; state: string }) {
  return (
    <>
      <span className="numeric" style={{ font: "700 11.5px 'Space Grotesk',sans-serif", color: 'var(--sub)', flex: 'none' }}>
        {refName}
      </span>
      <span style={{ flex: 1, minWidth: 0, font: '600 12.5px Manrope,sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
      <span style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', flex: 'none' }}>{state}</span>
    </>
  );
}

export function Facts({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div
      style={{
        background: 'var(--surface2)',
        borderRadius: 12,
        padding: 12,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: '5px 14px',
        font: '500 11.5px Manrope,sans-serif',
      }}
    >
      {rows.map(([k, v]) => (
        <FactRow key={k} k={k} v={v} />
      ))}
    </div>
  );
}

function FactRow({ k, v }: { k: string; v: string }) {
  return (
    <>
      <span style={{ color: 'var(--sub)' }}>{k}</span>
      <span style={{ fontWeight: 600 }}>{v}</span>
    </>
  );
}

export function Label({ children, sub }: { children: string; sub?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
      <span className="sectionLabel">{children}</span>
      {sub && <span style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>{sub}</span>}
    </div>
  );
}

export type { AlertVM };
